import * as React from 'react';
import { connect } from 'react-redux';
import { camelCase } from 'lodash';

import DeliverableMoveContent from './DeliverableMoveContent';

import { DELIVERABLE_ICONS, DELIVERABLE_TAG_COLORS_MAP, DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';

import { moveContent } from 'modules/subcampaigns/reducers/offerDeliverables';

import {
  Deliverable,
  DeliverableShort,
  MixedSelectDropdownItem,
  MixedSelectDropdownTag,
  RealmOutfit,
  Subcampaign
} from 'types';

type OwnProps = {
  outfits: Array<RealmOutfit>,
  deliverable: Deliverable,
  subcampaign: Subcampaign,
  deliverablesWithCA2: Array<DeliverableShort>,
  setDeliverableModified: (isModified: boolean) => void,
  onSave: () => void,
  onOpenMoveContent: (showMoveContent: boolean) => void
};

type DispatchProps = {
  dispatchMoveContent: (
    offerId: number,
    deliverableId: number,
    targetDeliverableId: number,
    assetIds: Array<string | number>
  ) => Promise<void>
};

type Props = OwnProps & DispatchProps;

type State = {
  selectedOutfitIds: Array<string | number>,
  selectedDeliverableId: number | null,
  isModified: boolean
};

class DeliverableMoveContentOutfits extends React.PureComponent<Props, State> {
  state: State = {
    selectedOutfitIds: [],
    selectedDeliverableId: null,
    isModified: false
  };

  getMenuItems(deliverablesWithCA2: Array<DeliverableShort>, selectedDeliverableId: number | null): Array<MixedSelectDropdownItem> {
    const { deliverable } = this.props;

    return deliverablesWithCA2.filter(d => (d.id !== deliverable.id && d.type === DELIVERABLE_TYPE_ZALANDO_OUTFIT)).map(d => {
      const dropdownTag = (d.tag?.name && d.tag?.color) ? ({
        label: d.tag.name,
        color: DELIVERABLE_TAG_COLORS_MAP[d.tag.color!]
      } as MixedSelectDropdownTag) : undefined;

      return ({
        id: d.id,
        identifier: String(d.id),
        value: String(d.id),
        label: d.name,
        isDisabled: false,
        isFocused: false,
        isSelected: String(d.id) === String(selectedDeliverableId),
        icon: {
          primary: { name: DELIVERABLE_ICONS[camelCase(d.type)] || 'instagram-custom-fg' }
        },
        tag: {
          secondary: dropdownTag
        }
      });
    });
  }

  getSelectedAttachments = () => {
    const { selectedOutfitIds } = this.state;
    const { outfits } = this.props;

    return outfits.map(outfit => {
      const selectedItem = selectedOutfitIds.find(id => id === outfit.id);

      return {
        ...outfit,
        isSelected: Boolean(selectedItem)
      };
    });
  };

  selectOutfit = (isSelected: boolean, outfitId: string | number) => {
    if (isSelected) {
      this.setState(state => ({
        selectedOutfitIds: [...state.selectedOutfitIds, outfitId]
      }));
      this.handleModification(true);
      return;
    }

    this.setState(state => ({
      selectedOutfitIds: state.selectedOutfitIds.filter(id => id !== outfitId)
    }));
  };

  handleModification = (isModified: boolean) => {
    const { setDeliverableModified } = this.props;
    this.setState({ isModified });
    setDeliverableModified(isModified);
  };

  handleChangeSelectedDeliverable = (
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ) => {
    const value = selectedItem?.identifier;
    this.setState({ selectedDeliverableId: value ? Number(value) : null });
    this.handleModification(true);
  };

  handleSave = async () => {
    const { deliverable, subcampaign, dispatchMoveContent, onSave } = this.props;
    const { selectedOutfitIds, selectedDeliverableId } = this.state;
    const { offer } = subcampaign;

    if (selectedDeliverableId) {
      await dispatchMoveContent(offer.id, deliverable.id, selectedDeliverableId, selectedOutfitIds);
      onSave();
    }
  };

  handleCancel = () => {
    this.setState({
      selectedOutfitIds: [],
      selectedDeliverableId: null
    });

    this.handleModification(false);
  };

  handleAttachmentsSelectionChange = (
    isSelected: boolean,
    outfitId?: number | string,
    event?: React.MouseEvent
  ) => {
    event?.preventDefault();

    if (!outfitId) {
      return;
    }

    this.selectOutfit(isSelected, outfitId);
  };

  handleSelectAll = (outfits: Array<RealmOutfit>) => {
    this.setState({
      selectedOutfitIds: outfits.map(a => a.id)
    });

    this.handleModification(true);
  };

  handleSelectDeselectToggle = (isSelected: boolean): void => {
    const { outfits } = this.props;

    if (isSelected) {
      this.handleSelectAll(outfits);
    }

    this.setState({
      selectedOutfitIds: []
    });
  };

  render() {
    const { selectedOutfitIds, selectedDeliverableId, isModified } = this.state;
    const { outfits, deliverablesWithCA2, onOpenMoveContent } = this.props;

    return (
      <DeliverableMoveContent
        outfits={outfits}
        isContentSelected={selectedOutfitIds.length >= 1}
        selectedDeliverableId={selectedDeliverableId}
        isModified={isModified}
        menuItems={this.getMenuItems(deliverablesWithCA2, selectedDeliverableId)}
        onContentSelectionChange={this.handleAttachmentsSelectionChange}
        onSelectDeselectToggle={this.handleSelectDeselectToggle}
        onSave={this.handleSave}
        onChangeSelectedDeliverable={this.handleChangeSelectedDeliverable}
        onCancel={this.handleCancel}
        onOpenMoveContent={onOpenMoveContent}
      />
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchMoveContent: (offerId, deliverableId, targetDeliverableId, assetIds) =>
    dispatch(moveContent(offerId, deliverableId, targetDeliverableId, assetIds))
});

export default connect(null, mapDispatchToProps)(DeliverableMoveContentOutfits);
